import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Download, FileText, Headphones, CheckCircle2, Mail, Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { getOrder, requestEmailDelivery } from "../lib/api";
import { trackPurchase, identifyUser } from "../lib/tiktok";
import { snapIdentify, snapPurchase } from "../lib/snapchat";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { getStripe } from "../lib/stripe";

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

// Polling backoff: 0s, 2s, 4s, 6s, 8s, 10s, 12s, 14s, 16s, 18s, 20s, 25s, 30s
// Total window ≈ 30s — enough for the vast majority of Stripe webhook deliveries.
const POLL_DELAYS_MS = [2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 5000, 5000];

export default function Success() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);
  const [emailValue, setEmailValue] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [delayedMessage, setDelayedMessage] = useState(false);

  // 3DS / external redirect — Stripe appends `payment_intent_client_secret` to
  // return_url. If present, we verify the PI status DIRECTLY with Stripe
  // (client-side) so the user sees success immediately even if the webhook
  // is delayed by 5–30s. Without this, paying users on 3DS-required cards
  // see "Confirming payment..." for the full polling window.
  useEffect(() => {
    const piClientSecret = searchParams.get("payment_intent_client_secret");
    if (!piClientSecret) return;
    let cancelled = false;
    (async () => {
      try {
        const stripeJs = await getStripe();
        if (!stripeJs || cancelled) return;
        const res = await stripeJs.retrievePaymentIntent(piClientSecret);
        const pi = res?.paymentIntent;
        if (!pi || cancelled) return;
        if (pi.status === "succeeded") {
          // Optimistically flip local state so the user immediately sees the
          // success UI + download link. Webhook will still flip DB asynchronously
          // and is the source of truth for fulfilment (email, TikTok S2S).
          setOrder((prev) => prev && prev.status === "Paid" ? prev : { ...(prev || { id: orderId }), status: "Paid", id: orderId });
        }
      } catch (_) { /* silent — fall back to polling */ }
    })();
    return () => { cancelled = true; };
  }, [searchParams, orderId]);

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const o = await getOrder(orderId);
        if (cancelled) return;
        // Merge so optimistic-paid client-side flip isn't overwritten by a stale Pending fetch.
        setOrder((prev) => (prev && prev.status === "Paid" && o.status !== "Paid") ? prev : o);
        const effectivePaid = o.status === "Paid" || (order && order.status === "Paid");
        if (o.email) {
          identifyUser({ email: o.email, externalId: orderId });
          snapIdentify({ email: o.email, pkg: o.package });
        }
        if (effectivePaid && !window.__bbPurchaseFired) {
          window.__bbPurchaseFired = true;
          trackPurchase(orderId, o.package);
          snapPurchase(o.package, orderId, o.email);
        }
        // Extended polling window (≈30s) covers webhook delivery spikes.
        if (o.status !== "Paid" && tries < POLL_DELAYS_MS.length) {
          const delay = POLL_DELAYS_MS[tries];
          setTimeout(() => setTries((t) => t + 1), delay);
          // After ~12s show a friendly "taking a bit longer" message so the user knows nothing is broken.
          if (tries >= 5) setDelayedMessage(true);
        }
      } catch (_) { /* network error — silent retry via tries counter */ }
    };
    fetchIt();
    return () => { cancelled = true; };
  }, [orderId, tries]);

  if (!order) {
    return <div className="min-h-screen flex items-center justify-center text-[#A1B0C6]">جاري التحميل...</div>;
  }

  const isPaid = order.status === "Paid";
  const isAI = order.package === "ai_work_upgrade" || order.is_ai === true;

  // ──────────── AI Work Upgrade — Executive Light Success (USA / English) ────────────
  if (isAI) {
    const megaUrl = "https://mega.nz/folder/J7VmiJQT#2vtlVxVVY-G8YSwmIzhM4A";
    return (
      <div className="min-h-screen bg-white text-[#111827] py-12 px-5 sm:px-8" dir="ltr" lang="en" style={{ fontFamily: "'Inter','SF Pro Display',system-ui,-apple-system,sans-serif" }} data-testid="ai-success-root">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 sm:p-12 text-center shadow-[0_30px_80px_-30px_rgba(15,23,42,0.15)]" data-testid="success-card">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-[#0F766E]/10 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-[#0F766E]" strokeWidth={1.5} />
              </div>
            </div>

            {isPaid ? (
              <>
                <h1 className="font-black text-2xl sm:text-4xl text-[#0F172A]">Thank you — payment successful</h1>
                <p className="mt-3 text-[#6B7280] text-sm">
                  Order <span className="text-[#0F172A] font-mono">{order.id.slice(0, 8)}</span>
                </p>

                <a
                  href={megaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ai-download-drive"
                  onClick={() => { try { window.gtag && window.gtag('event', 'ai_mega_clicked'); } catch (_) { /* gtag optional */ } }}
                  className="mt-9 inline-flex items-center justify-center gap-3 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-base sm:text-lg px-8 py-5 rounded-md transition-all shadow-[0_15px_40px_rgba(15,23,42,0.25)]"
                >
                  <Download className="w-5 h-5 text-[#C9A227]" />
                  <span>Access your course (direct download)</span>
                </a>

                <div className="mt-6 inline-flex items-center gap-2 text-xs text-[#6B7280]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0F766E]" />
                  Available immediately and permanently
                </div>

                {/* Email delivery — English */}
                <div className="mt-10 pt-8 border-t border-[#E5E7EB] text-left">
                  <div className="flex items-center gap-2 mb-2 justify-center">
                    <Mail className="w-4 h-4 text-[#C9A227]" />
                    <span className="text-sm font-bold text-[#0F172A]">Send the access link to your email (optional)</span>
                  </div>
                  <p className="text-xs text-[#6B7280] text-center mb-3 leading-relaxed">
                    Want the link on your email so you can come back to it later? Enter your address below.
                  </p>
                  {!emailSent ? (
                    <div className="flex flex-col sm:flex-row items-stretch gap-2 max-w-md mx-auto">
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={emailValue}
                        onChange={(e) => setEmailValue(e.target.value)}
                        className="bg-white border-[#E5E7EB] text-[#0F172A]"
                        dir="ltr"
                        data-testid="ai-email-input"
                      />
                      <Button
                        onClick={async () => {
                          if (!isValidEmail(emailValue)) { toast.error("Invalid email"); return; }
                          setSendingEmail(true);
                          try {
                            await requestEmailDelivery(orderId, emailValue.trim());
                            setEmailSent(true);
                            toast.success("Sent");
                          } catch { toast.error("Could not send. Please try again."); }
                          finally { setSendingEmail(false); }
                        }}
                        disabled={sendingEmail}
                        className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold"
                        data-testid="ai-email-send"
                      >
                        {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin"/> : "Send"}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center text-[#0F766E] text-sm font-semibold">✓ Link sent to your email</div>
                  )}
                </div>
              </>
            ) : (
              <>
                <h1 className="font-black text-2xl sm:text-4xl text-[#0F172A]">Confirming your payment</h1>
                <p className="mt-3 text-[#6B7280]">This usually takes just a few seconds. Your access link will appear here automatically.</p>
                {delayedMessage && (
                  <p className="mt-3 text-[13px] text-[#C9A227]" data-testid="ai-success-delayed-note">
                    Taking a bit longer than usual — your card has been charged and we&apos;re waiting for the final confirmation. Please keep this page open.
                  </p>
                )}
                <div className="mt-8 flex justify-center"><Loader2 className="w-7 h-7 text-[#0F172A] animate-spin"/></div>
              </>
            )}
          </div>

          <div className="mt-6 text-center text-xs text-[#6B7280]">
            AI Work Upgrade · Jackie Escalera
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#040914] text-white py-12 px-5 sm:px-8" dir="rtl">
      <div className="max-w-3xl mx-auto">
        <header className="flex items-center justify-center mb-10">
          <Logo className="h-16 w-16" />
        </header>

        <div className="bg-[#0B1D3A]/80 border border-[#C5A059]/30 rounded-sm p-8 sm:p-10 text-center fade-up" data-testid="success-card">
          <div className="flex justify-center mb-5">
            <div className="w-20 h-20 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/40 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-[#C5A059]" strokeWidth={1.5}/>
            </div>
          </div>

          {isPaid ? (
            <>
              <h1 className="font-display text-3xl sm:text-4xl font-black">شكراً لك — تم الدفع بنجاح</h1>
              <p className="mt-3 text-[#A1B0C6]">
                طلبك رقم <span className="text-white">{order.id.slice(0,8)}</span>
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {/* Pink (audio-only) shows just the audio card centered;
                    every other package shows the PDF + audio bundle. */}
                {order.downloads?.pdf_url ? (
                  <a
                    href={order.downloads?.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-pdf"
                    className="group flex flex-col items-center gap-2 bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-bold p-6 rounded-sm transition-colors"
                  >
                    <FileText className="w-7 h-7" strokeWidth={1.5}/>
                    <span>تحميل الكتاب (PDF)</span>
                    <Download className="w-4 h-4 mt-1"/>
                  </a>
                ) : null}

                {order.downloads?.audio_url ? (
                  <a
                    href={order.downloads.audio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-audio"
                    className={`group flex flex-col items-center gap-2 font-bold p-6 rounded-sm transition-colors ${
                      order.downloads?.pdf_url
                        ? "bg-transparent border-2 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10"
                        : "bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] sm:col-span-2"
                    }`}
                  >
                    <Headphones className="w-7 h-7" strokeWidth={1.5}/>
                    <span>{order.downloads?.pdf_url ? "تحميل الملحق الصوتي" : "تحميل البرنامج الصوتي كاملاً"}</span>
                    <Download className="w-4 h-4 mt-1"/>
                  </a>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 border border-dashed border-[#C5A059]/30 text-[#A1B0C6] p-6 rounded-sm text-sm">
                    <Headphones className="w-7 h-7" strokeWidth={1.5}/>
                    <span>الملحق الصوتي غير مضمّن في هذه النسخة</span>
                  </div>
                )}
              </div>

              <p className="mt-8 text-xs text-[#A1B0C6]">
                احتفظ بهذه الصفحة، يمكنك إعادة التحميل في أي وقت من نفس الرابط.
              </p>

              {/* Optional: send to email */}
              <div
                className="mt-8 border-t border-[#C5A059]/15 pt-6 text-right"
                data-testid="email-delivery-block"
                style={{ direction: "rtl" }}
              >
                <div className="flex items-center gap-2 mb-2 justify-center">
                  <Mail className="w-4 h-4 text-[#C5A059]" />
                  <span className="text-sm font-bold text-white">{order.package === "pink_audio" || order.is_pink ? "إرسال البرنامج الصوتي على بريدك الإلكتروني (اختياري)" : "إرسال الكتاب على بريدك الإلكتروني (اختياري)"}</span>
                </div>
                <p className="text-xs text-[#A1B0C6] text-center mb-3 leading-relaxed">
                  {order.package === "pink_audio" || order.is_pink
                    ? "لو تحبين يصلك الملف الصوتي على إيميلك للرجوع إليه لاحقاً، أدخلي بريدك هنا."
                    : "لو تحب يصلك ملفا الـ PDF والصوتي على إيميلك للرجوع إليهما لاحقاً، أدخل بريدك هنا."}
                </p>
                {emailSent ? (
                  <div className="text-sm text-[#C5A059] text-center" data-testid="email-sent-msg">
                    تم الإرسال بنجاح، تحقق من صندوق الوارد (وقد يصل إلى Promotions / Spam أحياناً).
                  </div>
                ) : (
                  <div className="flex gap-2 max-w-md mx-auto" style={{ direction: "ltr" }}>
                    <Input
                      type="email"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value.trim())}
                      placeholder="you@example.com"
                      autoComplete="email"
                      inputMode="email"
                      className="bg-white border-gray-300 text-[#040914] placeholder:text-gray-400 rounded-sm h-[42px]"
                      data-testid="post-purchase-email"
                    />
                    <Button
                      onClick={async () => {
                        if (!isValidEmail(emailValue)) {
                          toast.error("يرجى إدخال بريد إلكتروني صحيح");
                          return;
                        }
                        setSendingEmail(true);
                        try {
                          await requestEmailDelivery(orderId, emailValue);
                          setEmailSent(true);
                          toast.success("أُرسل الكتاب إلى بريدك");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "تعذّر الإرسال، حاول مرة أخرى.");
                        } finally {
                          setSendingEmail(false);
                        }
                      }}
                      disabled={sendingEmail}
                      data-testid="send-email-btn"
                      className="bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-bold px-5 rounded-sm shrink-0"
                    >
                      {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin"/> : "إرسال"}
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl sm:text-3xl font-black">جاري تأكيد الدفع...</h1>
              <p className="mt-3 text-[#A1B0C6]">
                نقوم بتأكيد عملية الدفع. عادة لا تستغرق العملية أكثر من بضع ثوانٍ.
              </p>
              <Button
                onClick={() => setTries((t) => t + 1)}
                className="mt-6 bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-bold px-6 py-5 rounded-sm"
                data-testid="recheck-status"
              >
                تحديث الحالة
              </Button>
            </>
          )}
        </div>

        <div className="text-center mt-8">
          <button onClick={() => navigate("/")} className="text-[#A1B0C6] hover:text-[#C5A059] text-sm">
            العودة للصفحة الرئيسية
          </button>
        </div>
      </div>
    </div>
  );
}
